/**
 * 通知升級判定純函式(9月交付 ticket 08)。
 *
 * 「給定規則＋事件時間軸＋現在時間 → 到現在為止該發過哪幾則、各是第幾階、
 * 該不該結案、結案該發哪則訊息」。mock 排程引擎(ticket 11)、設定頁與單元測試
 * 共用這一顆;函式不碰 Date.now、不碰 store —— 時間一律由呼叫端傳入,結果可重放。
 *
 * 放 helpers/ 而不是與頁面同住(uSlots 前例):首要消費者是 mock 排程引擎,
 * mock 層反向 import pages 會讓依賴方向打結;notifyMeta 已是同款前例。
 * 參數語意見 constants/notifyRules.js 的 NOTIFY_RULE_DEFAULTS 註解。
 */
import { NOTIFY_RULE_DEFAULTS, STAGE_PREFIX } from "../constants/notifyRules";

const UNIT_MS = { day: 86400000, minute: 60000 };

const toMs = (v) => (v == null ? null : typeof v === "number" ? v : new Date(v).getTime());

// 降級與設定錯誤都要吵(ADR-0004),但同一把鑰匙只吵一次 —— 排程引擎每輪都會
// evaluate 全部規則,不去重的 warn 會刷版到沒人看
const warned = new Set();
const warnOnce = (key, ...args) => {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(...args);
};

/** 該關卡有沒有被規則停用(缺設定時視為啟用,由 cadence 是否定義升階條件決定) */
const stageEnabled = (rule, level) => {
  const st = (rule.stages || []).find((s) => s.level === level);
  return st ? st.enabled !== false : true;
};

/** 想升到 wanted 階,但被停用的關卡往下收 —— 寧可留在低階持續提醒,也不無聲消失 */
const clampStage = (rule, wanted) => {
  let s = wanted;
  while (s > 1 && !stageEnabled(rule, s)) s -= 1;
  return s;
};

/**
 * @param rule    通知規則(buildNotifyRule 形狀)
 * @param context { startAt, dueAt?, resolvedAt? } — 條件成立/到期/恢復時間(ms 或 ISO)
 * @param now     現在時間(ms 或 ISO)
 * @returns { occurrences: [{ at, stage, prefix }], closed, closedAt, closeMsgKey }
 *          closeMsgKey 只在 closed=true 且該類別有結案訊息時有值(MSG 字典 key);
 *          審核類結案由 0A 系列自己發、到期類不另發 → null(見 NOTIFY_STOP_CONDITIONS)。
 */
export function evaluateNotifyRule(rule, context = {}, now) {
  const empty = { occurrences: [], closed: false, closedAt: null, closeMsgKey: null };
  if (!rule || rule.active === false) return empty;

  const cadence = rule.cadence || NOTIFY_RULE_DEFAULTS[rule.category];
  if (!cadence) {
    warnOnce(
      `category:${rule.category}`,
      `[notify] 規則「${rule.name || rule.id}」的類別「${rule.category}」沒有對應的頻率參數,`,
      "不會產生任何通知。請補進 constants/notifyRules.js 的 NOTIFY_RULE_DEFAULTS。"
    );
    return empty;
  }

  const startAt = toMs(context.startAt);
  const dueAt = toMs(context.dueAt);
  const resolvedAt = toMs(context.resolvedAt);
  const nowMs = toMs(now);
  if (nowMs == null || (cadence.beforeDue ? dueAt == null : startAt == null)) {
    warnOnce(
      `timeline:${rule.id ?? rule.name}`,
      `[notify] 規則「${rule.name || rule.id}」缺少時間軸(startAt/dueAt),無從判定,不產生通知。`
    );
    return empty;
  }

  let unit = UNIT_MS[cadence.unit];
  if (!unit) {
    // unit 打錯不能安靜當「天」了事:告警類(分鐘級)錯成天,頻率會差 1440 倍
    warnOnce(
      `unit:${rule.id ?? rule.name}:${cadence.unit}`,
      `[notify] 規則「${rule.name || rule.id}」的時間單位「${cadence.unit}」不認得` +
        "(認得:day/minute),暫以 day 計算 —— 請修正 cadence.unit。"
    );
    unit = UNIT_MS.day;
  }

  const closed = resolvedAt != null && resolvedAt <= nowMs;
  // 恢復後就停:只算恢復前的提醒
  const horizon = closed ? Math.min(resolvedAt, nowMs) : nowMs;

  const points = cadence.beforeDue
    ? duePoints(cadence, { startAt, dueAt }, horizon, unit)
    : intervalPoints(cadence, { startAt }, horizon, unit);

  const occurrences = points
    .filter(({ at }) => at <= horizon && (closed ? at < resolvedAt : true))
    .map(({ at, wanted }) => {
      const stage = clampStage(rule, wanted);
      return { at, stage, prefix: STAGE_PREFIX[stage] };
    });

  return {
    occurrences,
    closed,
    closedAt: closed ? resolvedAt : null,
    closeMsgKey: closed ? (rule.stopCondition && rule.stopCondition.closeMsgKey) || null : null,
  };
}

/** interval 族:等間隔提醒,靠「發滿 N 次」或「滿 N 天/分」升階 */
function intervalPoints(cadence, { startAt }, horizon, unit) {
  const { firstAfter = 0, repeatEvery, stage2, stage3 } = cadence;
  const first = startAt + firstAfter * unit;
  const step = repeatEvery * unit;
  const points = [];

  // 二階起點:滿次數 → 第 afterStage1Count 次之後的那一則;滿時間 → startAt + afterStart
  const stage2At =
    stage2 == null
      ? Infinity
      : stage2.afterStage1Count != null
        ? first + stage2.afterStage1Count * step
        : startAt + stage2.afterStart * unit;
  const stage3At =
    stage3 == null
      ? Infinity
      : stage3.afterStage2 != null
        ? stage2At + stage3.afterStage2 * unit
        : startAt + stage3.afterStart * unit;

  for (let t = first; t <= horizon; t += step) {
    const wanted = t >= stage3At ? 3 : t >= stage2At ? 2 : 1;
    points.push({ at: t, wanted });
    if (!(step > 0)) break; // repeatEvery 缺值或 0:只發一次,不無限迴圈
  }
  return points;
}

/** due 族:倒數提醒點+到期日二階+逾期三階;規則生效(startAt)前的點不補發 */
function duePoints(cadence, { startAt, dueAt }, horizon, unit) {
  const { beforeDue = [], stage2, stage3 } = cadence;
  const points = beforeDue.map((offset) => ({ at: dueAt - offset * unit, wanted: 1 }));
  if (stage2 && stage2.atDue) points.push({ at: dueAt, wanted: 2 });
  if (stage3 && stage3.afterDue != null) points.push({ at: dueAt + stage3.afterDue * unit, wanted: 3 });
  return points
    .filter(({ at }) => startAt == null || at >= startAt)
    .sort((a, b) => a.at - b.at);
}
